import React from 'react';
import styled from 'styled-components';
import { TranscriptElement } from '../../types.ts/transcript';
import { InferredTranscriptElement } from '../models/InferredTranscript';

interface TranscriptElementViewProps {
  element: TranscriptElement;
  onClick?: (e: React.MouseEvent<HTMLSpanElement>) => void;
  onMouseDown?: (e: React.MouseEvent<HTMLSpanElement>) => void;
  onMouseEnter?: () => void;
  onContextMenu?: (e: React.MouseEvent<HTMLSpanElement>) => void;
  onDoubleClick?: (e: React.MouseEvent<HTMLSpanElement>) => void;
  isActive: boolean;
  isSelected?: boolean;
  isCursorAfter?: boolean;
  showCursor?: boolean;
  /**
   * Whether this word should be visually highlighted because the playback
   * play‑head is currently inside its time span.
   */
  isHighlighted?: boolean;
}

// Note: We've moved this functionality to the useTranscriptStatus hook

/**
 * Component for rendering a single transcript element (word or punctuation)
 *
 * Wrapped in React.memo further below to avoid unnecessary re‑renders when
 * unrelated props stay the same (e.g. words that are not currently
 * highlighted/selected).
 */
const RawTranscriptElementView: React.FC<TranscriptElementViewProps> = ({
  element,
  onClick,
  onMouseDown,
  onMouseEnter,
  onContextMenu,
  onDoubleClick,
  isActive,
  isSelected = false,
  isCursorAfter = false,
  showCursor = true,
  isHighlighted = false,
}) => {
  const isText = element.type === 'text';
  const isPunct = element.type === 'punct';
  const isMuted = element.state === 'muted';
  const isReplaced = element.state === 'replaced';
  const isInferred = 'status' in element && (element as InferredTranscriptElement).status === 'removed';
  
  return (
    <ElementSpan
      onClick={isText ? onClick : undefined}
      onMouseDown={isText ? onMouseDown : undefined}
      onMouseEnter={isText ? onMouseEnter : undefined}
      onContextMenu={isText ? onContextMenu : undefined}
      onDoubleClick={isText ? onDoubleClick : undefined}
      isText={isText}
      isPunct={isPunct}
      isMuted={isMuted}
      isReplaced={isReplaced}
      isInferred={isInferred}
      isActive={isActive}
      isSelected={isSelected}
      isHighlighted={isHighlighted}
      isCursorAfter={isCursorAfter}
      showCursor={showCursor}
      value={element.value || ''}
      data-index={element.word_index !== undefined ? element.word_index : undefined}
    >
      {element.value}
      {isCursorAfter && showCursor && (
        <CursorSpan>|</CursorSpan>
      )}
    </ElementSpan>
  );
};

const CursorSpan = styled.span`
  display: inline-block;
  color: #fff;
  font-weight: bold;
  margin-left: 1px;
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  right: -2px;
  
  /* Remove animation since we're handling blink with MobX state */
  /* This gives us more control over the blink rate */
`;

interface ElementSpanProps {
  isText: boolean;
  isPunct: boolean;
  isMuted: boolean;
  isReplaced: boolean;
  isInferred: boolean; // Added for inferred transcript elements
  isActive: boolean;
  isSelected: boolean;
  /**
   * Whether the current playback head is inside the word's time span. Should
   * visually highlight the word (e.g. yellow background).
   */
  isHighlighted: boolean;
  isCursorAfter?: boolean;
  showCursor?: boolean;
  value?: string; // Added for styling based on value
}

const ElementSpan = styled.span<ElementSpanProps>`
  display: inline;
  color: ${props => {
    if (!props.isActive) return '#666';
    if (props.isMuted) return '#666';
    if (props.isReplaced) return '#00BFFF';
    if (props.isInferred) return '#FF9800'; // Orange color for inferred/removed words
    return '#fff';
  }};
  cursor: ${props => props.isText ? 'pointer' : 'default'};
  font-style: ${props => {
    if (props.isReplaced) return 'italic';
    if (props.isInferred) return 'italic'; // Italicize inferred words
    return 'normal';
  }};
  text-decoration: ${props => {
    if (props.isMuted) return 'line-through';
    if (props.isInferred) return 'dashed underline #FF9800'; // Dashed underline for inferred words
    return 'none';
  }};
  opacity: ${props => {
    if (!props.isActive) return 0.5;
    if (props.isMuted) return 0.4; // Make muted words more obviously faded
    if (props.isInferred) return 0.8; // Slightly dimmed for inferred words
    return 1;
  }};

  /* Background styling */
  background-color: ${props => {
    if (props.isSelected) return 'rgba(30, 58, 138, 0.7)'; // Brighter selected blue
    if (props.isHighlighted) return 'rgba(253, 224, 71, 0.4)'; // Yellow for karaoke style highlight
    if (props.isMuted) return 'rgba(255, 0, 0, 0.1)'; // Light red background for muted
    if (props.isInferred) return 'rgba(255, 152, 0, 0.1)'; // Light orange background for inferred
    return 'transparent';
  }};
  
  /* Add some spacing between words but not punctuation */
  margin-right: ${props => (!props.isPunct && props.value !== ' ') ? '2px' : '0'};
  
  /* Add some padding to make clicking and dragging easier */
  padding: ${props => props.isText ? '2px 2px' : '0'};
  
  /* Selection styling */
  border-radius: ${props => props.isSelected ? '3px' : '0'};
  transition: background-color 0.1s ease; /* Smooth transition for selection */
  user-select: none; /* Prevent text selection to enable better drag behavior */
  
  /* Better hover effect */
  &:hover {
    ${props => props.isText && !props.isSelected && `
      background-color: rgba(255, 255, 255, 0.15);
      border-radius: 3px;
    `}
  }
  
  /* Active selection indicator - makes it clearer which words are selected */
  ${props => props.isSelected && `
    position: relative;
    z-index: 1;
    box-shadow: 0 0 0 1px rgba(30, 58, 138, 0.3);
  `}
  
  /* Position relative so cursor can be positioned properly */
position: relative;
`;

// Memoize with shallow prop comparison. The majority of props are primitives
// so React.memo default comparison is sufficient. We still provide an explicit
// check just to be future‑proof.
const TranscriptElementView = React.memo(
  RawTranscriptElementView,
  (prev, next) => {
    // Check if both elements have status property and it's different
    const prevStatus = 'status' in prev.element ? (prev.element as InferredTranscriptElement).status : undefined;
    const nextStatus = 'status' in next.element ? (next.element as InferredTranscriptElement).status : undefined;

    return (
      prev.element === next.element && // referential equality for element object
      prev.isActive === next.isActive &&
      prev.isSelected === next.isSelected &&
      prev.isHighlighted === next.isHighlighted &&
      prev.isCursorAfter === next.isCursorAfter &&
      prev.showCursor === next.showCursor &&
      prevStatus === nextStatus // compare inferred status if it exists
    );
  },
);

export default TranscriptElementView;